import React, {ChangeEvent, FormEvent, useEffect, useState} from "react";
import localStyles from "./index.module.css";
import commonStyles from '../common/commonStyles/table.module.css';
import {
    Button,
    Checkbox,
    Dropdown,
    Form,
    Header,
    Icon,
    Loader,
    Menu,
    Message,
    Modal,
    Select,
    Table,
} from "semantic-ui-react";
import { Dept, Doctor, PatientsListItem, SpecialGroup } from "../dashboard";
import agent, {FilterOptions, PaginateOptions} from "../../agent";
import { Link } from "react-router-dom";
import {
    AppMode,
    DoctorTitleCase, GeneralSettingCase,
    GeneralTitleCase,
    getDoctorId,
    getDoctorTitle, getGeneralSetting,
    getGeneralTitle,
    getPatientTitle,
    PatientTitleCase,
} from "../../utils/common";
import { hasRole, UserRole } from "../../helpers/UserRole";
import { Utils } from "../../helpers/Utils";
import MediaQuery from "react-responsive";
import MassiveEventsModal from "./EditorTabs/MassiveEventModal";
import PatientsAnswerModal from "./EditorTabs/PatientsAnswerModal";
import ExportMassiveEventModal from "./EditorTabs/ExportMassiveEventModal";
import { TaskModel } from "../workTableTask/TaskModel";
import CreatedTaskDetailsModal from "./CreatedTaskDetailsModal";
import TablePage from "../general/TablePage";
import { textLabels } from "../../consts/textLabels";
import { useUrlPagination } from "../../hooks/useUrlPagination";
import ConfirmDeleteModal from "../common/ConfirmDeleteModal";
import {
    CommonListPagePaginationResponse,
    DEFAULT_LIMIT,
    DEFAULT_PAGE,
    handleSortPagination,
    ListState,
    ObjectReference,
} from "../../helpers/TablePageUtils";
import FilterPanel, {initialFilterState, PatientFilters} from './FilterPanel';
import {getEducationFormsOptions, getFundingSourceOptions} from "../../consts/tambsuConsts";
import {handleDatePickerKeyDown} from "../../helpers/CalendarEventsRepeater";
import {handleOnlyDigits, handleOnlyLettersAndSpecialChars} from "../../consts/onKeyDownValidation";
import MaskedDatePicker from "../general/MaskedDatePicker";
import {getPatientDeptOptions} from "../../consts/PatientConsts";

interface Props {
    match?: { url: string };
}

interface PatientPaginationResponse extends CommonListPagePaginationResponse<PatientsListItem> {}

type ExtendedListState = ListState<PatientsListItem> & {
    filters: PatientFilters;
};

const initialState: ExtendedListState = {
    page: DEFAULT_PAGE,
    limit: DEFAULT_LIMIT,
    sortColumn: "registerDate",
    filters: initialFilterState,
    docs: [],
    totalDocs: 0,
    totalPages: 1,
    loading: false,
    error: null,
};

export const firstLetterToUpperCase = (word: string): string => {
    return word.charAt(0).toUpperCase() + word.slice(1);
};

const PatientsList: React.FC = () => {
    const currentPath = "/dashboard/patients";

    const filterKeys = Object.keys(initialState.filters) as Array<keyof PatientFilters>;
    const customParams = filterKeys.map(key => `filters.${String(key)}`);

    const { state, setState, updateUrl } = useUrlPagination<ExtendedListState>(initialState, {
        defaultSortColumn: "registerDate",
        customParams,
    });

    const [depts, setDepts] = useState<Dept[]>([]);
    const [doctors, setDoctors] = useState<Doctor[]>([]);
    const [specialgroups, setSpecialGroups] = useState<SpecialGroup[]>([]);
    const [loadingRefs, setLoadingRefs] = useState(true);
    const [removeObject, setRemoveObject] = useState<ObjectReference | null>(null);
    const [removeManyCount, setRemoveManyCount] = useState<number | undefined>(undefined);
    const [selectedPatients, setSelectedPatients] = useState<ObjectReference[]>([]);
    const [openChangeDoctorModal, setOpenChangeDoctorModal] = useState(false);
    const [selectedChangeDoctorToId, setSelectedChangeDoctorToId] = useState<string | undefined>();
    const [selectedChangePatientDeptToId, setSelectedChangePatientDeptToId] = useState<string | undefined>();
    const [isChangeDoctorPatientDeptDisabled, setIsChangeDoctorPatientDeptDisabled] = useState(true);
    const [openCreateMassiveEventAll, setOpenCreateMassiveEventAll] = useState(false);
    const [openCreateMassiveEventParameters, setOpenCreateMassiveEventParameters] = useState(false);
    const [openCreateMassiveEventSelected, setOpenCreateMassiveEventSelected] = useState(false);
    const [openExportMassiveEvent, setOpenExportMassiveEvent] = useState(false);
    const [openCreatePatientsAnswer, setOpenCreatePatientsAnswer] = useState(false);
    const [createdTask, setCreatedTask] = useState<TaskModel | undefined>(undefined);
    const [showAllFilters, setShowAllFilters] = useState(false);
    const [activatedCount, setActivatedCount] = useState<number | null>(null);

    const selectedPatientIds = selectedPatients.map(p => p._id);

    const toArray = (value: string | string[] | undefined): string[] | undefined => {
        if (!value) return undefined;
        return Array.isArray(value) ? value : [value];
    };

    const loadReferenceData = async () => {
        setLoadingRefs(true);
        try {
            const paginateOptions: PaginateOptions = { limit: 10000, page: 1, search: "", sort: "surname" };
            const [deptsRes, doctorsRes, specialGroupsRes] = await Promise.all([
                agent.DeptProvider.getList(paginateOptions),
                agent.DoctorProvider.getList(paginateOptions),
                agent.SpecialGroupProvider.getList(),
            ]);
            setDepts(deptsRes.docs || []);
            setDoctors(doctorsRes.docs || []);
            setSpecialGroups(specialGroupsRes.docs || []);
        } catch (error) {
            console.error("Ошибка загрузки связанных данных:", error);
        } finally {
            setLoadingRefs(false);
        }
    };

    const fetchPatients = async () => {
        setState(prev => ({ ...prev, loading: true, error: null }));

        const {
            page,
            limit,
            sortColumn,
            filters,
        } = state;

        const {
            searchValue,
            activatedFilter,
            activatedDateFromFilter,
            activatedDateToFilter,
            registerDateFromFilter,
            registerDateToFilter,
            diagnosisFilter,
            telFilter,
            deptId,
            doctorId,
            specialGroupsIdsFilter,
            patientDeptsIdsFilter,
            typeFilter,
            instituteFilter,
            specializationFilter,
            courseFilter,
            educationFormFilter,
            fundingSourceFilter,
            structuralDivisionFilter,
        } = filters;

        const paginateOptions: PaginateOptions = {
            page,
            limit,
            sort: sortColumn,
            search: searchValue,
            deptId,
            doctorId,
            specialGroupsIdsFilter: toArray(specialGroupsIdsFilter),
            patientDeptsIdsFilter: toArray(patientDeptsIdsFilter),
            activatedFilter,
            activatedDateFromFilter: activatedDateFromFilter ? new Date(activatedDateFromFilter) : undefined,
            activatedDateToFilter: activatedDateToFilter ? new Date(activatedDateToFilter) : undefined,
            registerDateFromFilter: registerDateFromFilter ? new Date(registerDateFromFilter) : undefined,
            registerDateToFilter: registerDateToFilter ? new Date(registerDateToFilter) : undefined,
            diagnosisFilter,
            telFilter,
            isLargeFamilyFilter: typeFilter === "isLargeFamily" ? true : undefined,
            ...(window.config?.tambsu && {
                instituteFilter,
                specializationFilter,
                courseFilter,
                educationFormFilter,
                fundingSourceFilter,
                structuralDivisionFilter,
            }),
        };

        try {
            const response: PatientPaginationResponse = await agent.PatientProvider.getList(paginateOptions);
            if (response) {
                setState(prev => ({
                    ...prev,
                    docs: response.docs,
                    totalDocs: response.totalDocs,
                    totalPages: response.totalPages,
                    loading: false,
                    error: null,
                }));
            }
        } catch (error) {
            console.error("Ошибка получения:", error);
            setState(prev => ({
                ...prev,
                docs: [],
                totalDocs: 0,
                totalPages: 1,
                loading: false,
                error: `Не удалось загрузить список ${getPatientTitle(PatientTitleCase.ov)}. Пожалуйста, попробуйте позже.`,
            }));
        }
    };

    useEffect(() => {
        if (hasRole(UserRole.DOCTOR) && !state.filters.doctorId) {
            const doctorId = getDoctorId();
            if (doctorId) {
                setState(prev => ({...prev, filters: {...prev.filters, doctorId}}));
            }
        }
        loadReferenceData();
    }, []);


    useEffect(() => {
        fetchPatients();
    }, [
        state.page,
        state.limit,
        state.sortColumn,
        state.filters,
    ]);

    useEffect(() => {
        updateUrl();
    }, [
        state.page,
        state.limit,
        state.sortColumn,
        state.filters,
    ]);

    const fetchActivatedCount = () => {
        if (!getGeneralSetting(GeneralSettingCase.showActivatedCount)) {
            setActivatedCount(null);
            return;
        }

        const { filters } = state;
        const filterOptions: FilterOptions = {
            search: filters.searchValue,
            deptId: filters.deptId,
            doctorId: filters.doctorId,
            specialGroupsIdsFilter: toArray(filters.specialGroupsIdsFilter),
            patientDeptsIdsFilter: toArray(filters.patientDeptsIdsFilter),
            activatedFilter: filters.activatedFilter,
            activatedDateFromFilter: filters.activatedDateFromFilter ? new Date(filters.activatedDateFromFilter) : undefined,
            activatedDateToFilter: filters.activatedDateToFilter ? new Date(filters.activatedDateToFilter) : undefined,
            registerDateFromFilter: filters.registerDateFromFilter ? new Date(filters.registerDateFromFilter) : undefined,
            registerDateToFilter: filters.registerDateToFilter ? new Date(filters.registerDateToFilter) : undefined,
            diagnosisFilter: filters.diagnosisFilter,
            telFilter: filters.telFilter,
            isLargeFamilyFilter: filters.typeFilter === "isLargeFamily" ? true : undefined,
            ...(window.config?.tambsu && {
                instituteFilter: filters.instituteFilter,
                specializationFilter: filters.specializationFilter,
                courseFilter: filters.courseFilter,
                educationFormFilter: filters.educationFormFilter,
                fundingSourceFilter: filters.fundingSourceFilter,
                structuralDivisionFilter: filters.structuralDivisionFilter,
            }),
        };

        agent.PatientProvider.getActivatedCount(filterOptions)
            .then(response => setActivatedCount(response.activatedCount))
            .catch(() => setActivatedCount(null));
    };

    useEffect(() => {
        fetchActivatedCount();
    }, [state.filters]);

    const handleSearch = (filters: PatientFilters) => {
        setState(prev => ({
            ...prev,
            filters: { ...prev.filters, ...filters },
            page: DEFAULT_PAGE
        }));
    };

    const handleClearFilters = () => {
        const { typeFilter } = state.filters;
        const resetState: ExtendedListState = {
            ...initialState,
            filters: {
                ...initialState.filters,
                typeFilter,
                doctorId: hasRole(UserRole.DOCTOR) ? getDoctorId() ?? undefined : undefined
            },
            page: DEFAULT_PAGE
        };
        setState(resetState);
    };

    const handleDeselectHidden = () => {
        const visibleIds = new Set(docs.map(d => d._id));
        setSelectedPatients(prev => prev.filter(p => visibleIds.has(p._id)));
    };

    const handleDeselectAll = () => {
        setSelectedPatients([]);
    };

    const handleSort = (column: string) => {
        const newSort = handleSortPagination(state.sortColumn, column);
        setState(prev => ({ ...prev, sortColumn: newSort, page: DEFAULT_PAGE }));
    };

    const handlePageChange = (page: number) => {
        setState(prev => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setState(prev => ({ ...prev, limit, page: DEFAULT_PAGE }));
    };

    const openRemoveModalHandler = (obj: ObjectReference) => {
        setRemoveObject(obj);
    };

    const closeRemoveModal = () => {
        setRemoveObject(null);
    };

    const executeDelete = async (
        deleteAction: () => Promise<void>,
        onSuccess: () => void,
        errorMessage: string
    ) => {
        setState(prev => ({ ...prev, loading: true }));
        try {
            await deleteAction();
            onSuccess();
            fetchPatients();
            fetchActivatedCount();
        } catch (error) {
            console.error(errorMessage, error);
            setState(prev => ({
                ...prev,
                error: errorMessage,
            }));
        } finally {
            setState(prev => ({ ...prev, loading: false }));
        }
    };

    const removePatient = async () => {
        if (!removeObject) return;
        await executeDelete(
            () => agent.PatientProvider.remove(removeObject._id),
            () => {
                setSelectedPatients(prev => prev.filter(p => p._id !== removeObject._id));
                closeRemoveModal();
            },
            `Не удалось удалить ${getPatientTitle(PatientTitleCase.a)}. Пожалуйста, попробуйте позже.`
        );
    };

    const removeManyPatients = async () => {
        await executeDelete(
            () => agent.PatientProvider.removeMany(selectedPatientIds),
            () => {
                setSelectedPatients([]);
                setRemoveManyCount(undefined);
            },
            `Не удалось удалить выбранных ${getPatientTitle(PatientTitleCase.ov)}. Пожалуйста, попробуйте позже.`
        );
    };

    const exportPatientList = async () => {
        try {
            const task = await agent.PatientProvider.exportPatientList(hasRole(UserRole.DOCTOR) ? state.filters.doctorId : undefined);
            setCreatedTask(task);
        } catch (error) {
            console.error("Ошибка экспорта списка:", error);
        }
    };

    const exportCallHistory = async () => {
        try {
            const task = await agent.PatientProvider.exportCallHistory(hasRole(UserRole.DOCTOR) ? state.filters.doctorId : undefined);
            setCreatedTask(task);
        } catch (error) {
            console.error("Ошибка экспорта истории обращений:", error);
        }
    };

    const exportVeteranPatientList = async () => {
        try {
            let task = await agent.PatientProvider.exportVeteranPatientList(hasRole(UserRole.DOCTOR) ? state.filters.doctorId : undefined);
            setCreatedTask(task);
        } catch (error) {
            console.error("Ошибка экспорта:", error);
        }
    };

    const exportPatientCards = async () => {
        try {
            let task = await agent.PatientProvider.exportPatientCards(selectedPatientIds);
            setCreatedTask(task);
        } catch (error) {
            console.error("Ошибка экспорта:", error);
        }
    };

    const changePatientDeptButtonDisabledState = (doctorId?: string, patientDeptId?: string) => {
        const currentDoctorId = doctorId || selectedChangeDoctorToId;

        const hasDoctor = !!currentDoctorId;
        if (!hasDoctor) {
            setIsChangeDoctorPatientDeptDisabled(true);
            return;
        }

        const deptOptions = getPatientDeptOptions(doctors, currentDoctorId || "", depts);
        const hasDeptOptions = deptOptions.length > 0;
        const currentPatientDeptId = patientDeptId || selectedChangePatientDeptToId;
        const hasSelectedDept = !!currentPatientDeptId;
        setIsChangeDoctorPatientDeptDisabled(hasDeptOptions && !hasSelectedDept);
    };

    const addPrevPathToLink = (pathname: string) => {
        return {
            pathname,
            search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
        };
    };

    const {
        docs,
        totalDocs,
        totalPages,
        loading,
        error,
        page,
        limit,
        sortColumn,
    } = state;

    const {
        doctorId,
        specialGroupsIdsFilter,
        patientDeptsIdsFilter,
        typeFilter,
    } = state.filters;

    const visibleSelectedIds = new Set(docs.map(d => d._id));
    const hiddenSelectedCount = selectedPatientIds.filter(id => !visibleSelectedIds.has(id)).length;

    const styles = { ...commonStyles, ...localStyles };
    const headerLabel = `Список ${getPatientTitle(PatientTitleCase.ov)}`;
    const getMassiveEventLabel = `Список массовых рассылок`;
    const createLabel = textLabels.createButton + ` ${getPatientTitle(PatientTitleCase.a)}`;
    const confirmRemoveLabel = `Вы уверены, что хотите удалить ${getPatientTitle(PatientTitleCase.a)}`;
    const confirmRemoveManyLabel = `Вы уверены, что хотите удалить выбранных ${getPatientTitle(PatientTitleCase.ov)}`;
    const listLoaderLabel = textLabels.listLoaderMessage;
    const errorLabel = textLabels.errorMessage;

    return (
        <div className={styles.table}>
            {/* Модальные окна */}
            <ConfirmDeleteModal
                items={removeObject ? [removeObject] : []}
                confirmLabel={confirmRemoveLabel}
                onConfirm={removePatient}
                onClose={closeRemoveModal}
            />

            <ConfirmDeleteModal
                items={removeManyCount && selectedPatients.length > 0 ? selectedPatients : undefined}
                confirmLabel={confirmRemoveManyLabel}
                onConfirm={removeManyPatients}
                onClose={() => setRemoveManyCount(undefined)}
            />

            <Modal
                size="large"
                open={openChangeDoctorModal}
                onClose={() => {
                    setOpenChangeDoctorModal(false);
                    setIsChangeDoctorPatientDeptDisabled(true);
                    setSelectedChangeDoctorToId(undefined);
                    setSelectedChangePatientDeptToId(undefined);
                }}
                closeIcon
            >
                <Modal.Header>Перевод {getPatientTitle(PatientTitleCase.ov)}</Modal.Header>
                <Modal.Content>
                    <p>
                        Выполняя данную операцию, все отмеченные {getPatientTitle(PatientTitleCase.mult)} будут переведены выбранному Вами{" "}
                        {getDoctorTitle(DoctorTitleCase.u)} и в выбранн{getGeneralTitle(GeneralTitleCase.endingIm)} {getGeneralTitle(GeneralTitleCase.departmantim)} {getPatientTitle(PatientTitleCase.a)}.
                    </p>
                    <p>Вами отмечено {getPatientTitle(PatientTitleCase.ov)}: {selectedPatientIds.length}.</p>
                    <p>Кому перевести:</p>
                    <Form.Group widths="equal" style={{ display: "flex", gap: "10px" }}>
                        <Form.Field>
                            <Dropdown
                                selection
                                search
                                placeholder={firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.im))}
                                value={selectedChangeDoctorToId}
                                onChange={(_, data) => {
                                    const newDoctorId = data.value as string;
                                    setSelectedChangeDoctorToId(newDoctorId);
                                    setSelectedChangePatientDeptToId(undefined);
                                    changePatientDeptButtonDisabledState(newDoctorId, undefined);
                                }}
                                options={doctors.map((d) => ({ key: d._id, text: `${d.surname} ${d.name} ${d.middlename}`, value: d._id }))}
                                noResultsMessage={textLabels.noResultsMessage}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Select
                                key={selectedChangeDoctorToId}
                                search
                                placeholder={`${firstLetterToUpperCase(getGeneralTitle(GeneralTitleCase.departmantim))} ${getPatientTitle(PatientTitleCase.a)}`}
                                value={selectedChangePatientDeptToId}
                                options={getPatientDeptOptions(doctors, selectedChangeDoctorToId || "", depts).map((opt) => ({ value: opt.value, text: opt.text }))}
                                onChange={(_, { value }) => {
                                    const newDeptId = value as string;
                                    setSelectedChangePatientDeptToId(newDeptId);
                                    changePatientDeptButtonDisabledState(selectedChangeDoctorToId, newDeptId);
                                }}
                                disabled={!selectedChangeDoctorToId}
                                noResultsMessage={textLabels.noResultsMessage}
                            />
                        </Form.Field>
                    </Form.Group>
                </Modal.Content>
                <Modal.Actions>
                    <Button
                        disabled={isChangeDoctorPatientDeptDisabled}
                        content="Перевести"
                        onClick={async () => {
                            if (!selectedChangeDoctorToId) return;

                            const deptOptions = getPatientDeptOptions(doctors, selectedChangeDoctorToId || "", depts);
                            const hasDeptOptions = deptOptions.length > 0;

                            if (hasDeptOptions && !selectedChangePatientDeptToId) return;

                            await agent.PatientProvider.changePatientsDoctor({
                                doctorId: selectedChangeDoctorToId,
                                patientDeptId: hasDeptOptions ? selectedChangePatientDeptToId : undefined,
                                patientIds: selectedPatientIds,
                            });
                            setOpenChangeDoctorModal(false);
                            setIsChangeDoctorPatientDeptDisabled(true);
                            setSelectedChangeDoctorToId(undefined);
                            setSelectedChangePatientDeptToId(undefined);
                            setSelectedPatients([]);
                            fetchPatients();
                        }}
                        positive
                    />
                    <Button color="grey" onClick={() => setOpenChangeDoctorModal(false)}>
                        {textLabels.cancelButton}
                    </Button>
                </Modal.Actions>
            </Modal>

            {createdTask && <CreatedTaskDetailsModal task={createdTask} onClose={() => setCreatedTask(undefined)} />}

            <div className={styles.header}>
                <Header>{headerLabel}</Header>
                {!hasRole(UserRole.OBSERVER) && (
                    <Link to={`${currentPath}/create`}>
                        <Button className={styles.createButton} size="large" color="green" type="submit">
                            {createLabel}
                        </Button>
                    </Link>
                )}
            </div>

            {error && (
                <Message negative>
                    <Message.Header>{errorLabel}</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            <div className={styles.list}>
                <FilterPanel
                    initialFilters={state.filters}
                    onSearch={handleSearch}
                    onReset={handleClearFilters}
                    selectedCount={selectedPatientIds.length}
                    onDeselectHidden={handleDeselectHidden}
                    onDeselectAll={handleDeselectAll}
                    page={state.page}
                />
                {(loading || loadingRefs) ? (
                    <Loader active>{listLoaderLabel}</Loader>
                ) : (
                    <>
                        <Menu>
                            {!hasRole(UserRole.OBSERVER) && (
                                <Dropdown text="Выполнить" pointing className="link item">
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={exportPatientList}>
                                        Экспорт списка {getPatientTitle(PatientTitleCase.ov)}
                                    </Dropdown.Item>
                                    {window.config?.vladimir &&
                                        <Dropdown.Item
                                            onClick={exportVeteranPatientList}>Экспорт демобилизованных</Dropdown.Item>
                                    }
                                    <Dropdown.Item onClick={exportCallHistory}>
                                        Экспорт истории обращений {getPatientTitle(PatientTitleCase.ov)}
                                    </Dropdown.Item>
                                    {selectedPatientIds.length > 0 && (
                                        <Dropdown.Item onClick={exportPatientCards}>
                                            Экспорт карточек выбранных {getPatientTitle(PatientTitleCase.ov)}
                                        </Dropdown.Item>
                                    )}
                                    {hasRole(UserRole.ADMIN) && (
                                        <>
                                            <Dropdown.Item onClick={() => setOpenExportMassiveEvent(true)}>
                                                Экспорт массовых рассылок
                                            </Dropdown.Item>
                                            {selectedPatientIds.length > 0 && (
                                                <Dropdown.Item
                                                    onClick={() => setOpenChangeDoctorModal(true)}
                                                >
                                                    Перевести выбранных {getPatientTitle(PatientTitleCase.ov)} другому {getDoctorTitle(DoctorTitleCase.u)}
                                                </Dropdown.Item>
                                            )}
                                            {selectedPatientIds.length > 0 && (
                                                <Dropdown.Item
                                                    onClick={() => setRemoveManyCount(selectedPatientIds.length)}
                                                >
                                                    Удалить выбранных {getPatientTitle(PatientTitleCase.ov)}
                                                </Dropdown.Item>
                                            )}
                                            <Dropdown.Item onClick={() => setOpenCreateMassiveEventAll(true)}>
                                                Создать массовую рассылку для всех
                                            </Dropdown.Item>
                                            {selectedPatientIds.length > 0 && (
                                                <Dropdown.Item onClick={() => setOpenCreateMassiveEventSelected(true)}>
                                                    Создать массовую рассылку для<br />выбранных
                                                </Dropdown.Item>
                                            )}
                                            {(doctors.length > 0 || specialgroups.length > 0) && (
                                                <Dropdown.Item
                                                    onClick={() => {
                                                        setOpenCreateMassiveEventParameters(true);
                                                    }}
                                                >
                                                    Создать массовую рассылку<br />по параметрам
                                                </Dropdown.Item>
                                            )}
                                            {window.config?.useExportPatientAnswer === true && (
                                                <Dropdown.Item onClick={() => setOpenCreatePatientsAnswer(true)}>
                                                    Экспорт ответов {getPatientTitle(PatientTitleCase.ov)}
                                                </Dropdown.Item>
                                            )}
                                        </>
                                    )}
                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                        {(hasRole(UserRole.ADMIN) || hasRole(UserRole.OBSERVER)) && (
                            <Menu.Item as={Link} to="patientMassiveEvent/getList" className="link item">
                                {getMassiveEventLabel}
                            </Menu.Item>
                        )}
                    </Menu>

                    <Menu tabular>
                        <Menu.Item
                            content="Все"
                            active={typeFilter === "all"}
                            onClick={() => setState(prev => ({
                                ...prev,
                                filters: { ...prev.filters, typeFilter: "all" },
                                page: DEFAULT_PAGE
                            }))}
                        />
                        <Menu.Item
                            content="Многодетные семьи"
                            active={typeFilter === "isLargeFamily"}
                            onClick={() => setState(prev => ({
                                ...prev,
                                filters: { ...prev.filters, typeFilter: "isLargeFamily" },
                                page: DEFAULT_PAGE
                            }))}
                        />
                    </Menu>

                    <Table stackable selectable compact sortable striped verticalAlign="middle" color="blue">
                        <Table.Header className={styles.tableHeader}>
                            <Table.Row>
                                {hasRole(UserRole.ADMIN) && (
                                    <Table.HeaderCell textAlign="center">
                                        <Checkbox
                                            checked={docs.length > 0 && docs.every(p => selectedPatientIds.includes(p._id))}
                                            onChange={(_, data) => {
                                                const isChecked = !!data.checked;
                                                const currentPageIds = docs.map(p => p._id);
                                                if (isChecked) {
                                                    setSelectedPatients(prev => {
                                                        const existingIds = new Set(prev.map(p => p._id));
                                                        const newPatients = docs
                                                            .filter(d => !existingIds.has(d._id))
                                                            .map(d => ({ _id: d._id, name: `${d.surname} ${d.name} ${d.middlename}` }));
                                                        return [...prev, ...newPatients];
                                                    });
                                                } else {
                                                    setSelectedPatients(prev => prev.filter(p => !currentPageIds.includes(p._id)));
                                                }
                                            }}
                                        />
                                    </Table.HeaderCell>
                                )}
                                <Table.HeaderCell
                                    sorted={sortColumn === "surname" || sortColumn === "-surname"
                                        ? sortColumn.startsWith("-") ? "descending" : "ascending"
                                        : undefined}
                                    onClick={() => handleSort("surname")}
                                >
                                    <Header as="h5" textAlign="left">ФИО</Header>
                                </Table.HeaderCell>
                                <MediaQuery minWidth={1224}>
                                    <Table.HeaderCell width="2">
                                        <Header as="h5">
                                            {Utils.vomode() !== AppMode.STUDENT
                                                ? firstLetterToUpperCase(getDoctorTitle(DoctorTitleCase.im))
                                                : "Карьерный советник"}
                                        </Header>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell><Header as="h5">Информация для Вас</Header></Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <Header as="h5">
                                            {Utils.vomode() !== AppMode.PATIENT ? (
                                                <>
                                                    Дата встречи с<br />
                                                    {getDoctorTitle(DoctorTitleCase.om)}
                                                </>
                                            ) : (
                                                "Госпитализация"
                                            )}
                                        </Header>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === "diagnosis" || sortColumn === "-diagnosis"
                                            ? sortColumn.startsWith("-") ? "descending" : "ascending"
                                            : undefined}
                                        onClick={() => handleSort("diagnosis")}
                                    >
                                        <Header as="h5" textAlign="left">
                                            {Utils.vomode() !== AppMode.PATIENT ? (
                                                <>
                                                    Описание<br />
                                                    ситуации
                                                </>
                                            ) : (
                                                "Диагноз"
                                            )}
                                        </Header>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell>
                                        <Header as="h5">
                                            {Utils.vomode() !== AppMode.STUDENT ? (
                                                <>
                                                    Особые<br />группы
                                                </>
                                            ) : (
                                                "Курсы"
                                            )}
                                        </Header>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === "activated" || sortColumn === "-activated"
                                            ? sortColumn.startsWith("-") ? "descending" : "ascending"
                                            : undefined}
                                        onClick={() => handleSort("activated")}
                                    >
                                        <Header as="h5">Активирован</Header>
                                    </Table.HeaderCell>
                                    <Table.HeaderCell
                                        sorted={sortColumn === "dateofbirth" || sortColumn === "-dateofbirth"
                                            ? sortColumn.startsWith("-") ? "descending" : "ascending"
                                            : undefined}
                                        onClick={() => handleSort("dateofbirth")}
                                    >
                                        <Header as="h5">Дата<br />рождения</Header>
                                    </Table.HeaderCell>
                                </MediaQuery>
                                <Table.HeaderCell><Header as="h5">Телефон</Header></Table.HeaderCell>
                                {hasRole(UserRole.ADMIN) && <Table.HeaderCell />}
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {docs.map((el) => (
                                <Table.Row style={{ cursor: "pointer" }} key={`row-${el._id}`}>
                                    {hasRole(UserRole.ADMIN) && (
                                        <Table.Cell textAlign="center">
                                            <Checkbox
                                                checked={selectedPatientIds.includes(el._id)}
                                                onChange={(_, data) => {
                                                    const checked = !!data.checked;
                                                    setSelectedPatients(prev =>
                                                        checked
                                                            ? [...prev, { _id: el._id, name: `${el.surname} ${el.name} ${el.middlename}` }]
                                                            : prev.filter(p => p._id !== el._id)
                                                    );
                                                }}
                                            />
                                        </Table.Cell>
                                    )}
                                    <Table.Cell>
                                        <Link
                                            to={{
                                                pathname: `${currentPath}/${el._id}/edit/patientInfo`,
                                                search: `?returnUrl=${encodeURIComponent(window.location.href)}`,
                                            }}
                                            target="_blank"
                                        >
                                            {`${el.surname} ${el.name} ${el.middlename}`}
                                        </Link>
                                    </Table.Cell>
                                    <MediaQuery minWidth={1224}>
                                        <Table.Cell>{el.doctorName}</Table.Cell>
                                        <Table.Cell>
                                            <Link className={styles.linkCell} to={addPrevPathToLink(`${currentPath}/${el._id}/edit/patientInfo`)} target="_blank">
                                                {el.rheumatologistVisitDate ? new Date(el.rheumatologistVisitDate).toLocaleDateString("ru") : ""}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Link className={styles.linkCell} to={addPrevPathToLink(`${currentPath}/${el._id}/edit/patientInfo`)} target="_blank">
                                                {el.hospitalizationDate ? new Date(el.hospitalizationDate).toLocaleDateString("ru") : ""}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell title={el.diagnosis}>{el.diagnosis?.substring(0, 50)}</Table.Cell>
                                        <Table.Cell
                                            title={
                                                el.specialgroupids
                                                    ? specialgroups
                                                        .filter((sg) => el.specialgroupids?.includes(sg._id))
                                                        .map((sg) => sg.name)
                                                        .join("; ")
                                                    : ""
                                            }
                                        >
                                            {el.specialgroupids
                                                ? specialgroups
                                                    .filter((sg) => el.specialgroupids?.includes(sg._id))
                                                    .map((sg) => sg.name)
                                                    .join("; ")
                                                    .substring(0, 20)
                                                : ""}
                                        </Table.Cell>
                                        <Table.Cell>{el.activated ? "Да" : "Нет"}</Table.Cell>
                                        <Table.Cell>
                                            <Link className={styles.linkCell} to={addPrevPathToLink(`${currentPath}/${el._id}/edit/patientInfo`)} target="_blank">
                                                {el.dateofbirth ? new Date(el.dateofbirth).toLocaleDateString("ru") : ""}
                                            </Link>
                                        </Table.Cell>
                                    </MediaQuery>
                                    <Table.Cell>
                                        <Link className={styles.linkCell} to={addPrevPathToLink(`${currentPath}/${el._id}/edit/patientInfo`)} target="_blank">
                                            {el.tel}
                                        </Link>
                                    </Table.Cell>
                                    {hasRole(UserRole.ADMIN) && (
                                        <Table.Cell>
                                            <Button icon
                                                    onClick={(e) => {
                                                        openRemoveModalHandler({ _id: el._id, name: Utils.getFullName(el.surname, el.name, el.middlename) });
                                                    }}
                                            >
                                                <Icon color="red" name="trash alternate outline" />
                                            </Button>
                                        </Table.Cell>
                                    )}
                                </Table.Row>
                            ))}
                        </Table.Body>

                        <Table.Footer>
                            <Table.Row>
                                {hasRole(UserRole.ADMIN) && (
                                    <Table.HeaderCell>
                                        <div></div>
                                    </Table.HeaderCell>
                                )}
                                <Table.HeaderCell>
                                    {getGeneralSetting(GeneralSettingCase.showActivatedCount) && activatedCount !== null && (
                                        <h5>Всего активировано: {activatedCount}</h5>
                                    )}
                                </Table.HeaderCell>
                                <Table.HeaderCell colSpan="10">
                                    <Menu floated="right" pagination>
                                        <TablePage
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalDocs={totalDocs}
                                            limit={limit}
                                            onPageChange={handlePageChange}
                                            onLimitChange={handleLimitChange}
                                        />
                                        {selectedPatientIds.length > 0 && (
                                            <Menu.Item>
                                                {hiddenSelectedCount > 0
                                                    ? `Отмечено: ${selectedPatientIds.length} (скрыто фильтром: ${hiddenSelectedCount})`
                                                    : `Отмечено: ${selectedPatientIds.length}`}
                                            </Menu.Item>
                                        )}
                                    </Menu>
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Footer>
                    </Table>

                    {/* Модальные окна действий */}
                    {openCreateMassiveEventSelected && (
                        <MassiveEventsModal
                            type="selected"
                            patientIds={selectedPatientIds}
                            onClose={() => setOpenCreateMassiveEventSelected(false)}
                        />
                    )}
                    {openCreateMassiveEventAll && (
                        <MassiveEventsModal
                            type="all"
                            patientIds={[]}
                            onClose={() => setOpenCreateMassiveEventAll(false)}
                        />
                    )}
                    {openCreateMassiveEventParameters && (
                        <MassiveEventsModal
                            type="parameters"
                            doctor={doctorId}
                            specialgroups={toArray(specialGroupsIdsFilter)}
                            patientDepts={toArray(patientDeptsIdsFilter)}
                            patientIds={[]}
                            onClose={() => setOpenCreateMassiveEventParameters(false)}
                        />
                    )}
                    {openCreatePatientsAnswer && (
                        <PatientsAnswerModal
                            onClose={(task) => {
                                setOpenCreatePatientsAnswer(false);
                                setCreatedTask(task);
                            }}
                        />
                    )}
                    {openExportMassiveEvent && (
                        <ExportMassiveEventModal
                            onClose={(task) => {
                                setOpenExportMassiveEvent(false);
                                setCreatedTask(task);
                            }}
                        />
                    )}
                    </>
                )}
            </div>
        </div>
    );
};

export default PatientsList;
